"use client";
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { setUserLocale } from "@/locales/locale";
import Cookies from "js-cookie";

interface LanguageContextType {
  language: string;
  isLoading: boolean;
  setLanguage: (lang: string) => void;
  changeLanguage: (lang: string) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState("ar");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Get initial language from cookie
    const languageCookie = Cookies.get("language");
    if (languageCookie) {
      setLanguageState(languageCookie);
    }
  }, []);

  const setLanguage = (lang: string) => {
    setLanguageState(lang);
    Cookies.set("language", lang);
  };

  const changeLanguage = async (lang: string) => {
    setIsLoading(true);

    try {
      setLanguage(lang);
      await setUserLocale(lang as "ar" | "en");

      // Trigger a custom event that components can listen to
      window.dispatchEvent(new CustomEvent("languageChanged", { detail: { language: lang } }));

      // Add a small delay to ensure all components have time to refetch
      await new Promise(resolve => setTimeout(resolve, 1000));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, isLoading, setLanguage, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
